import { GetStaticPaths, GetStaticProps } from 'next'
import { ReactElement } from 'react'

import { dehydrate, QueryClient } from 'react-query'

import { getHelpCenterArticleSlugs } from 'app/services/Api/helpCenter'
import { helpCenterConfig } from 'app/services/Query/help-center/help-center.query'
import { NextPage } from 'app/types/decalarations/next'
import { HELP_CENTER_CATEGORY_PARAMS } from 'app/utils/constants/help-center.constants'

import HelpCenterLayout from '~/components/layouts/HelpCenterLayout'
import MainLayout from '~/components/layouts/MainLayout'
import HelpCenterArticleView from '~/views/HelpCenter/HelpCenterArticleView'

const HelpCenterDetailPage: NextPage = () => <HelpCenterArticleView />

HelpCenterDetailPage.getLayout = (page: ReactElement) => (
  <MainLayout>
    <HelpCenterLayout>{page}</HelpCenterLayout>
  </MainLayout>
)

export const getStaticPaths: GetStaticPaths = async () => {
  const { slugs } = await getHelpCenterArticleSlugs()

  const paths =
    slugs.map(({ slug, category_slug }) => ({
      params: { slug, category: category_slug }
    })) ?? []

  return { paths, fallback: true }
}

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const articleSlug = context?.params?.slug as string
    const categorySlug = context?.params?.category as string

    const queryClient = new QueryClient()
    const { getHelpCenterCategories, getHelpCenterArticleDetail } =
      helpCenterConfig

    const getHelpCenterCategoriesKey = getHelpCenterCategories.key(
      HELP_CENTER_CATEGORY_PARAMS
    )

    await queryClient.prefetchQuery(
      getHelpCenterCategoriesKey,
      getHelpCenterCategories.request
    )

    const categoriesState = queryClient.getQueryState<
      HelpCenterCategoryModel[]
    >(getHelpCenterCategoriesKey)

    const categories = categoriesState?.data
    const currentCategory = categories?.find((el) => el.slug === categorySlug)

    if (!currentCategory) throw new Error('Category not found')

    const articleDetailQueryKey = getHelpCenterArticleDetail.key(articleSlug)

    await queryClient.prefetchQuery(
      articleDetailQueryKey,
      getHelpCenterArticleDetail.request
    )

    const state = queryClient.getQueryState(articleDetailQueryKey)

    if (state?.status === 'error') throw new Error(String(state?.error))

    return {
      props: { dehydratedState: dehydrate(queryClient) }
    }
  } catch (error) {
    return { notFound: true }
  }
}

export default HelpCenterDetailPage
